import React, { useState, useEffect } from 'react';
import { DateParserUtil, DateTimeObject, DateInputFormat } from '../../lib/parsers/dateParserUtil';

export const DateConverter: React.FC = () => {
  const [input, setInput] = useState('');
  const [inputFormat, setInputFormat] = useState<DateInputFormat>('auto');
  const [timezone, setTimezone] = useState<string>(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [result, setResult] = useState<DateTimeObject | null>(null);
  const [error, setError] = useState<string>('');

  const converter = new DateParserUtil({ inputFormat, timezone });

  useEffect(() => {
    if (input.trim()) {
      const parsedResult = converter.parse(input);
      setResult(parsedResult);
      
      if (!parsedResult.isValid) {
        setError('Invalid date input');
      } else {
        setError('');
      }
    } else {
      setResult(null);
      setError('');
    }
  }, [input, inputFormat, timezone]);

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const handlePresetClick = (preset: string) => {
    setInput(preset);
  };

  const handleCurrentTimeClick = () => {
    const now = new Date();
    setInput(now.toISOString());
  };

  const formats = DateParserUtil.getAvailableFormats();
  const timezones = DateParserUtil.getAvailableTimezones();
  const currentTimestamps = DateParserUtil.getCurrentTimestamps();

  return (
    <div className="date-converter" role="main">
      <h1>Date Converter</h1>
      <p>Convert dates and timestamps between different formats and timezones.</p>

      <div className="input-section">
        <label htmlFor="date-input">Date Input:</label>
        <input
          id="date-input"
          type="text"
          placeholder="Enter date or timestamp..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          aria-describedby="date-help"
          data-testid="date-input"
        />
        <small id="date-help">
          Supports Unix timestamps, ISO 8601, and various date formats
        </small>
      </div>

      <div className="options-section">
        <div className="option-group">
          <label htmlFor="input-format">Input Format:</label>
          <select
            id="input-format"
            value={inputFormat}
            onChange={(e) => setInputFormat(e.target.value as DateInputFormat)}
            data-testid="input-format-select"
          >
            {formats.map(format => (
              <option key={format.value} value={format.value}>
                {format.label} - {format.description}
              </option>
            ))}
          </select>
        </div>

        <div className="option-group">
          <label htmlFor="timezone">Timezone:</label>
          <select
            id="timezone"
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            data-testid="timezone-select"
          >
            {timezones.map(tz => (
              <option key={tz.value} value={tz.value}>
                {tz.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="presets-section">
        <h3>Quick Presets</h3>
        <div className="presets">
          <button onClick={() => handleCurrentTimeClick()}>
            Current Time
          </button>
          <button onClick={() => handlePresetClick('2023-03-15T10:30:00Z')}>
            ISO 8601 Example
          </button>
          <button onClick={() => handlePresetClick('1678878600')}>
            Unix Timestamp
          </button>
          <button onClick={() => handlePresetClick('03-15-2023')}>
            MM-DD-YYYY
          </button>
          <button onClick={() => handlePresetClick('15-03-2023')}>
            DD-MM-YYYY
          </button>
          <button onClick={() => handlePresetClick('2023-03-15')}>
            YYYY-MM-DD
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message" role="alert" data-testid="error-message">
          {error}
        </div>
      )}

      {result && result.isValid && (
        <div className="results" data-testid="date-results">
          <div className="result-section">
            <h3>Conversion Results</h3>
            <div className="result-grid">
              <div className="result-item">
                <label>Detected Format:</label>
                <span data-testid="detected-format">{result.format}</span>
              </div>
              <div className="result-item">
                <label>Unix Timestamp (seconds):</label>
                <span data-testid="unix-timestamp">{result.unixTimestamp}</span>
                <button
                  onClick={() => result.unixTimestamp && handleCopy(result.unixTimestamp.toString())}
                  className="copy-button"
                  aria-label="Copy Unix timestamp"
                >
                  Copy
                </button>
              </div>
              <div className="result-item">
                <label>ISO 8601:</label>
                <span data-testid="iso8601">{result.iso8601}</span>
                <button
                  onClick={() => result.iso8601 && handleCopy(result.iso8601)}
                  className="copy-button"
                  aria-label="Copy ISO 8601"
                >
                  Copy
                </button>
              </div>
              <div className="result-item">
                <label>Local Time ({result.timezone}):</label>
                <span data-testid="local-time">{result.humanReadableLocal}</span>
                <button
                  onClick={() => result.humanReadableLocal && handleCopy(result.humanReadableLocal)}
                  className="copy-button"
                  aria-label="Copy local time"
                >
                  Copy
                </button>
              </div>
              <div className="result-item">
                <label>UTC Time:</label>
                <span data-testid="utc-time">{result.humanReadableUTC}</span>
                <button
                  onClick={() => result.humanReadableUTC && handleCopy(result.humanReadableUTC)}
                  className="copy-button"
                  aria-label="Copy UTC time"
                >
                  Copy
                </button>
              </div>
            </div>
          </div>

          <div className="actions">
            <button
              onClick={() => {
                const allResults = [
                  `Input: ${result.rawValue}`,
                  `Format: ${result.format}`,
                  `Unix Timestamp: ${result.unixTimestamp}`,
                  `ISO 8601: ${result.iso8601}`,
                  `Local Time: ${result.humanReadableLocal}`,
                  `UTC Time: ${result.humanReadableUTC}`
                ].join('\n');
                handleCopy(allResults);
              }}
              data-testid="copy-all-button"
            >
              Copy All Results
            </button>
          </div>
        </div>
      )}

      <div className="current-timestamps-section">
        <h3>Current Timestamps</h3>
        <div className="timestamps-grid">
          {Object.entries(currentTimestamps).map(([label, value]) => (
            <div key={label} className="timestamp-item">
              <label>{label}:</label>
              <span>{value}</span>
              <button
                onClick={() => handleCopy(value)}
                className="copy-button"
                aria-label={`Copy ${label}`}
              >
                Copy
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};